/**
 * LiveBehavioralOperations — Admin → Strategy
 * ------------------------------------------------------------------
 * Operational war-room view of every active deployment campaign:
 * deployment state, saturation, replay velocity, behavioral decay,
 * fatigue warnings, and audience conditioning score. Live-updated
 * via realtime + custom deployment events.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Activity, Radio, AlertTriangle, ScanLine, Rocket, Skull } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  DEPLOYMENT_EVENT,
  STATUS_TONE,
  fetchDeploymentCampaigns,
  runDecayScan,
  setCampaignStatus,
  stageDeploymentsFromCampaigns,
  type DeploymentCampaignRow,
} from "@/lib/deploymentEngine";
import { toast } from "@/hooks/use-toast";

export default function LiveBehavioralOperations() {
  const [items, setItems] = useState<DeploymentCampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [staging, setStaging] = useState(false);

  const load = async () => {
    setItems(await fetchDeploymentCampaigns());
    setLoading(false);
  };

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener(DEPLOYMENT_EVENT, refresh);
    const ch = (supabase as any)
      .channel("deploy-camps")
      .on("postgres_changes", { event: "*", schema: "public", table: "deployment_campaigns" }, refresh)
      .subscribe();
    return () => {
      window.removeEventListener(DEPLOYMENT_EVENT, refresh);
      (supabase as any).removeChannel(ch);
    };
  }, []);

  const handleStage = async () => {
    setStaging(true);
    const n = await stageDeploymentsFromCampaigns();
    setStaging(false);
    toast({ title: n ? `${n} campaign(s) staged for deployment` : "Nothing new to stage" });
  };

  const handleScan = async () => {
    setScanning(true);
    const sigs = await runDecayScan();
    setScanning(false);
    toast({
      title: "Decay scan complete",
      description: sigs.length ? `${sigs.length} behavioral signal(s) detected` : "No fatigue signatures detected",
    });
  };

  const live = items.filter((i) => ["live", "deployed", "saturating", "declining"].includes(i.deployment_status)).length;

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Radio className="w-5 h-5 text-cyan animate-pulse" />
            Live Behavioral Operations
            <Badge variant="outline" className="ml-2 text-[10px] border-cyan/40 text-cyan">{live} live</Badge>
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={staging} onClick={handleStage}
              className="h-8 text-xs border-cyan/40 text-cyan hover:bg-cyan/10">
              <Rocket className="w-3 h-3 mr-1" /> Stage Campaigns
            </Button>
            <Button size="sm" variant="outline" disabled={scanning} onClick={handleScan}
              className="h-8 text-xs border-amber-400/40 text-amber-300 hover:bg-amber-400/10">
              <ScanLine className={`w-3 h-3 mr-1 ${scanning ? "animate-pulse" : ""}`} /> Run Decay Scan
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="text-xs text-muted-foreground">Scanning operations grid...</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-muted-foreground italic">
            No deployments staged. Run <span className="text-cyan">Stage Campaigns</span> to pull active behavioral campaigns into the operational layer.
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="rounded border border-glass-border bg-charcoal-deep/60 p-3 space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-foreground font-medium truncate">
                  {c.dominant_platform ?? "multi-platform"} · campaign {c.campaign_id?.slice(0, 8) ?? "—"}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                  {c.launched_at ? `launched ${new Date(c.launched_at).toLocaleString()}` : "not launched"}
                  {c.retired_at && ` · retired ${new Date(c.retired_at).toLocaleString()}`}
                </div>
              </div>
              <Badge className={`text-[10px] uppercase tracking-wider ${STATUS_TONE[c.deployment_status]}`}>
                {c.deployment_status}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
              <Metric label="Reach" value={c.projected_reach} tone="cyan" />
              <Metric label="Replay Velocity" value={c.projected_replay_velocity} tone="emerald" />
              <Metric label="Conditioning" value={c.audience_conditioning_score} tone="violet" />
              <Metric
                label="Saturation"
                value={c.behavioral_saturation}
                tone={c.behavioral_saturation > 70 ? "red" : c.behavioral_saturation > 45 ? "amber" : "cyan"}
              />
            </div>

            <div className="flex items-center justify-between gap-2 text-[10px]">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Activity className="w-3 h-3 text-cyan" /> conf
                  <span className="text-cyan font-mono">{Math.round(c.deployment_confidence)}%</span>
                </span>
                {c.fatigue_risk > 60 && (
                  <span className="flex items-center gap-1 text-amber-300">
                    <AlertTriangle className="w-3 h-3" /> fatigue {Math.round(c.fatigue_risk)}%
                  </span>
                )}
                {c.decay_score > 30 && (
                  <span className="flex items-center gap-1 text-orange-300">
                    <Skull className="w-3 h-3" /> decay {Math.round(c.decay_score)}
                  </span>
                )}
              </div>
              <div className="flex gap-1">
                {c.deployment_status === "staged" && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-cyan/40 text-cyan"
                    onClick={() => setCampaignStatus(c.id, "deployed")}>Deploy</Button>
                )}
                {(c.deployment_status === "deployed" || c.deployment_status === "scheduled") && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-400/40 text-emerald-300"
                    onClick={() => setCampaignStatus(c.id, "live")}>Mark Live</Button>
                )}
                {c.deployment_status !== "retired" && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-500/40 text-red-400"
                    onClick={() => setCampaignStatus(c.id, "retired")}>Retire</Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: "cyan" | "emerald" | "violet" | "amber" | "red" }) {
  const t = {
    cyan: "text-cyan", emerald: "text-emerald-300", violet: "text-violet-300", amber: "text-amber-300", red: "text-red-400",
  }[tone];
  return (
    <div className="rounded bg-charcoal-medium/60 border border-glass-border px-2 py-1.5">
      <div className="uppercase tracking-wider text-muted-foreground text-[9px]">{label}</div>
      <div className={`font-mono text-sm ${t}`}>{Math.round(value)}%</div>
      <Progress value={Math.min(100, value)} className="h-0.5 mt-1" />
    </div>
  );
}
